import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';

import { Badge, Card, MotionNumber } from '../ui';
import { useRealtime } from '../../hooks/useRealtime';
import type { RealtimeEnvelope } from '../../lib/realtime';
import { useHlBalance, useMe, useMyPmMarkets } from '../../hooks/useApiQuery';
import { useHlRealtimePositions } from '../../hooks/useHlRealtimePositions';
import { isRawMarketHash } from '../../lib/format';
import type { HlPositionItem, PmMarketForMe, PositionItem } from '../../lib/types';
import { OceanStrip } from './OceanStrip';

interface Props {
  /** Pre-loaded open position (server REST). Realtime events update on top. */
  position: PositionItem | null;
  /**
   * When true, render without the outer Card frame, market-title row,
   * or OceanStrip footer — used when PriceToBeat embeds this body
   * inside the unified Cove block (chip strip + countdown already
   * rendered by the parent). Standalone consumers (HL positions on
   * the dashboard) get the full self-contained card.
   */
  headless?: boolean;
}

interface FilledPayload {
  position_id?: string;
  avg_fill_price?: string;
}

interface PricePayload {
  up_bid?: string;
  down_bid?: string;
  market_id?: string;
}

const num = (s?: string | null) => {
  if (s === null || s === undefined || s === '') return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
};

export function ActiveTrade({ position, headless = false }: Props) {
  const { t } = useTranslation();
  const me = useMe();
  const pmMarkets = useMyPmMarkets();
  const botEnabled = me.data?.bot_enabled ?? true;
  // Local snapshot — REST seeds it, realtime updates on top so the
  // entry price + filled price update without a refetch.
  const [snapshot, setSnapshot] = useState<PositionItem | null>(position);
  const [bids, setBids] = useState<{ up: number | null; down: number | null }>({
    up: null,
    down: null,
  });
  // Server time anchor for the PM epoch-end check. Backend stamps
  // every realtime envelope with ``ts`` (unix seconds, ``time.time()``
  // in apps/api/.../realtime.py). We pin the most-recently-seen
  // server ts together with the local wall-clock at which we received
  // it; the effective "now" used by the freeze gate is then
  // ``serverTs + (Date.now() − localReceivedAt)`` so:
  //
  //   - clock-drift on the device is corrected (#193 P2 from Codex);
  //   - between ticks we still advance forward in time monotonically,
  //     so a 30-second gap with no ticks doesn't leave the gate
  //     thinking we're still at the moment of the last tick (background
  //     → foreground edge case);
  //   - a reordered or replayed tick with an *older* server ts can't
  //     rewind the anchor — the monotonicity guard below drops it.
  //
  // Falls back to ``Date.now()`` until the first tick lands.
  const [serverAnchor, setServerAnchor] = useState<{
    serverTsMs: number;
    localReceivedAtMs: number;
  } | null>(null);
  useEffect(() => setSnapshot(position), [position]);

  useRealtime('global:price', (env: RealtimeEnvelope) => {
    const p = env.payload as PricePayload;
    // PR #311: ``global:price`` now fans out a separate envelope per
    // market. Without filtering, every card would overwrite its bids
    // with whatever market ticked last — a user with concurrent PM
    // positions on btc-5m + eth-5m would see PnL flicker between the
    // two. Pre-#311 envelopes have no ``market_id``; fold under
    // ``btc-5m`` so a downgraded engine keeps the BTC card working.
    // Server ts is still a valid wall-clock anchor for the freeze
    // gate regardless of which market the envelope belongs to, so
    // ``serverAnchor`` updates unconditionally below.
    const payloadMarket = p.market_id ?? 'btc-5m';
    const cardMarket = snapshot?.market_id ?? null;
    if (cardMarket === null || payloadMarket === cardMarket) {
      setBids({ up: num(p.up_bid), down: num(p.down_bid) });
    }
    if (!Number.isFinite(env.ts)) return;
    const incoming = env.ts * 1000;
    setServerAnchor((prev) => {
      // Monotonicity guard: drop out-of-order frames whose server ts
      // is older than what we already pinned. WS preserves order in
      // norm, but a brief disconnect → buffered replay can deliver
      // older frames after newer ones; trusting them blindly would
      // rewind the anchor and let an actually-ended epoch slip back
      // into "active" for the duration of the offset.
      if (prev && incoming <= prev.serverTsMs) return prev;
      return { serverTsMs: incoming, localReceivedAtMs: Date.now() };
    });
  });

  // Always-mounted HL data fetches — must come BEFORE the early
  // return below, otherwise a settle event flips ``snapshot`` to null
  // and React errors with "rendered fewer hooks than expected" when
  // the hook count changes between renders. ``enabled`` gates the
  // network call so PM-only sessions never hit /v1/balance/hl or
  // open the HL public WebSocket.
  const isHlSnapshot = snapshot?.bot_kind === 'hyperliquid';
  const hlBalance = useHlBalance({ enabled: isHlSnapshot });
  // Live mark price + uPnL from HL's public ``webData2`` push channel.
  // Drives sub-second updates while the position is open; falls back
  // to the 15-second REST polling above when the WS hasn't connected
  // yet (first paint, transient drop, mock without driver).
  const { positions: hlRealtimePositions } = useHlRealtimePositions({
    enabled: isHlSnapshot,
  });

  useRealtime(
    (ch) => ch.endsWith(':trade'),
    (env: RealtimeEnvelope) => {
      const p = env.payload as FilledPayload;
      // Only react to events for *this* card's position. The
      // ``opened`` event used to synthesize a fresh snapshot here so
      // the card lit up before the next /v1/positions refetch — but
      // with the dashboard now mounting one ActiveTrade per open
      // position, that broadcast overwrote every card with the new
      // position's id (Codex P1 on PR #188). New positions surface
      // via the ``useRealtimeInvalidator`` hook, which already
      // invalidates the ``positions`` query on every ``:trade`` event
      // so the parent's ``openPositions`` list refetches and mounts a
      // fresh card automatically.
      if (env.type === 'filled' && p.position_id && p.avg_fill_price) {
        setSnapshot((prev) =>
          prev && prev.id === p.position_id
            ? { ...prev, entry_price: p.avg_fill_price! }
            : prev,
        );
      }
      if ((env.type === 'settled' || env.type === 'cancelled') && p.position_id) {
        setSnapshot((prev) => (prev && prev.id === p.position_id ? null : prev));
      }
    },
  );

  if (!snapshot) {
    // Empty-state — bot enabled but no live position. Standalone
    // consumers (Dashboard HL fallback, etc.) get a small self-
    // contained card with a sleeping-mode strip. Headless consumers
    // (PriceToBeat embedding) skip entirely — the PTB scanning body
    // already covers "no position" UX.
    if (headless) return null;
    return (
      <Card pad="md" variant="paper">
        <p className="pb-eyebrow">{t('dashboard.active_trade_label')}</p>
        <p className="pb-caption mt-2">
          {botEnabled
            ? t('dashboard.active_trade_none')
            : t('dashboard.active_trade_paused')}
        </p>
        <OceanStrip mode={botEnabled ? 'hunting' : 'sleeping'} />
      </Card>
    );
  }

  const sideUp = /up|long/i.test(snapshot.side);
  const sideLabel = sideUp ? t('dashboard.side_up') : t('dashboard.side_down');
  const isPm = snapshot.bot_kind === 'polymarket';
  const isHl = snapshot.bot_kind === 'hyperliquid';
  // Label priority: registry display_name (PM) → non-hash Position.market
  // (HL uses this — it's the coin symbol) → generic bot label.
  const pmSpec: PmMarketForMe | undefined =
    isPm && snapshot.market_id
      ? pmMarkets.data?.items.find((m) => m.market_id === snapshot.market_id)
      : undefined;
  const marketLabel = pmSpec
    ? pmSpec.display_name
    : snapshot.market && !isRawMarketHash(snapshot.market)
      ? snapshot.market
      : t(isPm ? 'dashboard.market_pm' : 'dashboard.market_hl');

  // Prefer the realtime WebSocket snapshot when available — it ticks
  // sub-second on HL state changes. Fall through to the polling
  // ``useHlBalance`` snapshot during connection setup / drop / mock
  // sessions without a driver. Both shapes are ``HlPositionItem[]``
  // so the lookup is identical.
  const hlPositionsLive = hlRealtimePositions ?? hlBalance.data?.positions;
  const hlPosition: HlPositionItem | undefined =
    isHl && hlPositionsLive
      ? hlPositionsLive.find((p) => p.coin === snapshot.market)
      : undefined;

  // PM 5-min epochs are aligned to :00/:05/:10/... — once the epoch
  // ends, the position is awaiting on-chain CTF resolution + the
  // ``settle_sweep`` worker (~30-60s after close per
  // workers/trading_engine/.../settle_sweep.py). During that window
  // the row is still ``status=open`` server-side, but ``global:price``
  // is now ticking the *next* epoch's bids — so showing live PnL
  // against those would be wildly misleading. Freeze the live cells
  // until the realtime ``settled`` event clears the card via the
  // existing handler below. Use server-side ``ts`` as the time anchor
  // (Codex P2 on PR #193) so a device with a drifting clock can't
  // prematurely freeze a still-active epoch — and project forward
  // from the last anchor so a quiet stretch (Telegram WebView
  // backgrounding the WS) doesn't pin "now" to whenever the last
  // tick arrived.
  const effectiveNowMs = serverAnchor
    ? serverAnchor.serverTsMs + (Date.now() - serverAnchor.localReceivedAtMs)
    : Date.now();
  // Registry first; parse the documented kebab convention as fallback
  // (covers btc-5m, eth-15m, … so a removed-while-open btc-15m doesn't
  // get frozen 10 min early). If both miss we return null and skip the
  // freeze gate entirely — better to keep live cells ticking until the
  // ``settled`` event lands than to freeze at the wrong time.
  const pmEpochLenSec =
    pmSpec?.timeframe_sec ?? epochLenFromMarketId(snapshot.market_id);
  const pmEpochEnded =
    isPm &&
    pmEpochLenSec !== null &&
    pmEpochHasEnded(snapshot.opened_at, effectiveNowMs, pmEpochLenSec);

  const currentPrice = pmEpochEnded
    ? null
    : isPm
      ? sideUp
        ? bids.up
        : bids.down
      : isHl && hlPosition
        ? num(hlPosition.mark_price)
        : null;
  const pnl = pmEpochEnded
    ? null
    : isHl
      ? hlPosition
        ? num(hlPosition.unrealized_pnl)
        : null
      : computePnlNow(snapshot, currentPrice);

  const body = (
    <>
      <div className="flex items-center justify-between gap-2">
        <Badge tone={sideUp ? 'success' : 'danger'} dot>
          {sideLabel}
        </Badge>
        {/* The market-title row is owned by the parent when headless
            (PriceToBeat header), so we only print it in standalone mode. */}
        {headless ? null : (
          <p className="truncate text-[13px] font-medium text-tg-text">
            {marketLabel}
          </p>
        )}
      </div>

      {pmEpochEnded ? (
        // Round closed → settle pending. Drop the live grid entirely
        // (current price + PnL are locked at "—" anyway) and collapse
        // entry/stake into a single recap line under the banner; less
        // visual noise during the ~30-60s settle window.
        <>
          <p className="pb-caption mt-2 rounded-pb-sm bg-pb-paper2 px-2 py-1.5 text-tg-hint">
            {t('dashboard.awaiting_settle')}
          </p>
          <p className="pb-caption mt-2 text-[12px] text-tg-text">
            {t('dashboard.trade_recap', {
              stake: Number(snapshot.notional_usdc).toFixed(2),
              side: sideLabel,
              entry: Number(snapshot.entry_price).toFixed(2),
            })}
          </p>
        </>
      ) : (
        <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2 text-[12px]">
          <Row
            label={t('dashboard.trade_entry')}
            value={
              <MotionNumber
                value={Number(snapshot.entry_price)}
                format="number"
              />
            }
          />
          <Row
            label={t('dashboard.trade_stake')}
            value={
              <MotionNumber
                value={Number(snapshot.notional_usdc)}
                format="usd"
              />
            }
          />
          <Row
            label={t('dashboard.trade_current_price')}
            value={
              currentPrice !== null ? (
                <MotionNumber
                  value={currentPrice}
                  format="number"
                  mode="smooth"
                />
              ) : (
                <span className="text-tg-hint">—</span>
              )
            }
          />
          <Row
            label={t('dashboard.trade_pnl_now')}
            value={
              pnl !== null ? (
                <MotionNumber
                  value={pnl}
                  format="usd-signed"
                  mode="smooth"
                  className={clsx(
                    pnl > 0
                      ? 'text-pb-success'
                      : pnl < 0
                        ? 'text-pb-danger'
                        : 'text-tg-text',
                  )}
                />
              ) : (
                <span className="text-tg-hint">—</span>
              )
            }
          />
        </dl>
      )}
    </>
  );

  if (headless) return <div className="mt-4">{body}</div>;

  return (
    <Card pad="md" variant="paper">
      <p className="pb-eyebrow">{t('dashboard.active_trade_label')}</p>
      {body}
      <OceanStrip
        mode="guarding"
        confidence={confidenceFromPnl(pnl, Number(snapshot.notional_usdc))}
      />
    </Card>
  );
}

/** Same depth signal used by the OceanStrip footer, lifted out so the
 * PriceToBeat wrapper can drive its own strip when embedding ActiveTrade
 * headlessly. Exported for that single internal consumer. */
export function confidenceForOcean(
  pnl: number | null,
  notional: number,
): number {
  return confidenceFromPnl(pnl, notional);
}

/**
 * Map current PnL to a 0..1 confidence the whale uses for depth in
 * guarding mode. ``+notional/4`` (i.e. +25% on the stake) is "right at
 * the surface"; ``−notional/4`` is "at the seabed"; missing PnL pins
 * the whale at neutral 0.5.
 */
function confidenceFromPnl(pnl: number | null, notional: number): number {
  if (pnl === null || !Number.isFinite(notional) || notional <= 0) return 0.5;
  const ratio = pnl / (notional * 0.25);
  return Math.max(0, Math.min(1, 0.5 + ratio * 0.5));
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <dt className="pb-eyebrow !text-[10px] !tracking-[0.07em]">{label}</dt>
      <dd className={clsx('pb-numeral mt-0.5 text-[14px] text-tg-text')}>
        {value}
      </dd>
    </div>
  );
}

/**
 * True between an epoch's wall-clock end and ``settle_sweep`` writing
 * the final on-chain numbers (~30-60s later, see
 * ``workers/trading_engine/.../settle_sweep.py``). The row stays
 * ``status=open`` during that gap, but ``global:price`` is already
 * ticking the next epoch's bids — computing live PnL against those
 * would be wildly misleading, so we freeze the live cells.
 */
function pmEpochHasEnded(
  openedAt: string | null,
  nowMs: number,
  epochLenSec: number,
): boolean {
  if (!openedAt) return false;
  const openedMs = Date.parse(openedAt);
  if (!Number.isFinite(openedMs)) return false;
  const epochLenMs = epochLenSec * 1000;
  const epochEndMs =
    Math.floor(openedMs / epochLenMs) * epochLenMs + epochLenMs;
  return nowMs > epochEndMs;
}

/**
 * Recover the epoch length from a market_id when the registry lookup
 * misses (admin removed the market while a position was still open).
 * Matches the documented kebab convention from
 * ``polybot_engine.pm.market_spec.MarketSpec.market_id``: e.g.
 * ``btc-5m`` → 300, ``eth-15m`` → 900, ``foo-90s`` → 90. Returns null
 * for anything outside the convention so the caller can skip the
 * freeze gate rather than guessing wrong (and freezing 10 min early
 * on a btc-15m position because of a 5-min default).
 */
const MARKET_ID_TIMEFRAME_RE = /-(\d+)(m|s|h)$/;
function epochLenFromMarketId(marketId: string | null): number | null {
  if (!marketId) return null;
  const match = MARKET_ID_TIMEFRAME_RE.exec(marketId);
  if (!match) return null;
  const n = Number(match[1]);
  if (!Number.isFinite(n) || n <= 0) return null;
  switch (match[2]) {
    case 's':
      return n;
    case 'm':
      return n * 60;
    case 'h':
      return n * 3600;
  }
  return null;
}

/**
 * Mark-to-market PnL for a Polymarket binary outcome:
 *   size_tokens * current_bid - notional_usdc.
 * Falls back to deriving size from notional/entry when the realtime
 * "opened" payload hasn't filled it in yet.
 */
function computePnlNow(p: PositionItem, currentPrice: number | null): number | null {
  if (currentPrice === null || currentPrice <= 0) return null;
  if (p.bot_kind !== 'polymarket') return null;
  const notional = Number(p.notional_usdc);
  if (!Number.isFinite(notional) || notional <= 0) return null;
  let size = Number(p.size);
  if (!Number.isFinite(size) || size <= 0) {
    const entry = Number(p.entry_price);
    if (!Number.isFinite(entry) || entry <= 0) return null;
    size = notional / entry;
  }
  return size * currentPrice - notional;
}
